/**
 * AddWaitingListPopup
 *
 * Redesigned create/edit modal for waiting list entries.
 *
 * UX structure:
 *   Section 1 — Patient and appointment interest (patient, procedure, professional)
 *   Section 2 — When can the patient come? (days + period toggles)
 *   Section 3 — Advanced options (collapsed by default: specific hours, dates, priority, notes)
 *
 * The UI maps period selections (Manhã/Tarde/Noite/Qualquer) to the backend's
 * preferredTimes array of hour strings (e.g. ["08:00","09:00",...]).
 *
 * Modality (attendance_mode + meeting_url) follows the appointment rules and is
 * carried into the appointment created from the waiting list.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Select, MenuItem, FormControl, InputLabel,
  CircularProgress, ListItemText, Alert, Collapse,
  IconButton, Button, Autocomplete,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import { DataProviderInstance } from '../../api/DataProvider';
import styles from './AddWaitingListPopup.module.css';
import AttendanceModeSelector from '../../pages/appointments/v2/components/AttendanceModeSelector/AttendanceModeSelector';
import {
  ATTENDANCE_IN_PERSON, ATTENDANCE_ONLINE, normalizeAttendanceMode, isValidMeetingUrl, MEETING_URL_ERROR,
} from '../../pages/appointments/v2/utils/attendanceMode';

// ── Period definitions ────────────────────────────────────────────────────────

const PERIODS = [
  { id: 'morning',   label: 'Manhã',           sub: '8h – 12h',  startH: 8,  endH: 11 },
  { id: 'afternoon', label: 'Tarde',            sub: '12h – 18h', startH: 12, endH: 17 },
  { id: 'evening',   label: 'Noite',            sub: '18h – 22h', startH: 18, endH: 22 },
  { id: 'any',       label: 'Qualquer horário', sub: null,        startH: null, endH: null },
];

const DAY_LABELS = {
  1: 'Seg', 2: 'Ter', 3: 'Qua', 4: 'Qui', 5: 'Sex', 6: 'Sáb', 0: 'Dom',
};

function getCurrentDateString() {
  return new Date().toISOString().split('T')[0];
}
function getDefaultEndDateString(daysLimit = 14) {
  const d = new Date();
  d.setDate(d.getDate() + daysLimit);
  return d.toISOString().split('T')[0];
}

/** Returns future appointments for a given patientId from the appointments array.
 *  The appointments array uses `a.patient` (numeric patient id) not `a.patientId`.
 */
function getFutureAppointments(appointments, patientId) {
  if (!appointments || !patientId) return [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return appointments.filter(a => {
    // appointments use `patient` field (numeric id), not `patientId`
    const apptPatient = a.patient ?? a.patientId;
    if (String(apptPatient) !== String(patientId)) return false;
    try {
      const dateStr = typeof a.date === 'string' ? a.date.split(' ')[0] : a.date;
      const d = new Date(dateStr + 'T00:00:00');
      return d >= today;
    } catch { return false; }
  });
}

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Returns which period ids are active given a list of hour strings */
function detectPeriodsFromTimes(times, allTimes) {
  if (!times || times.length === 0) return [];
  if (times.length === allTimes.length) return ['any'];
  const active = [];
  PERIODS.filter(p => p.id !== 'any').forEach(p => {
    const periodHours = allTimes.filter(t => {
      const h = parseInt(t);
      return h >= p.startH && h <= p.endH;
    });
    if (periodHours.length > 0 && periodHours.every(h => times.includes(h))) {
      active.push(p.id);
    }
  });
  return active;
}

/** Expands period ids into hour strings using available times */
function expandPeriodsToTimes(periodIds, allTimes) {
  if (periodIds.includes('any')) return [...allTimes];
  const result = new Set();
  periodIds.forEach(pid => {
    const p = PERIODS.find(x => x.id === pid);
    if (!p) return;
    allTimes.forEach(t => {
      const h = parseInt(t);
      if (h >= p.startH && h <= p.endH) result.add(t);
    });
  });
  return [...result].sort();
}

// ── Component ─────────────────────────────────────────────────────────────────

function AddWaitingListPopup({ OnHide, OnSaved, initialData, availabilityParams }) {
  const {
    GetPatientById, GetPatients, CompanyConfig, UserData,
    SetWaitingList, UpdateWaitingList, appointments,
  } = DataProviderInstance();

  const availableDaysOfWeek = CompanyConfig.AvailableTime ? CompanyConfig.AvailableDaysOfWeek || [] : [];
  const availableTimeRange = CompanyConfig.AvailableTime || [8, 18];
  const blockedTimes = CompanyConfig.BlockedTimes || [];

  // Generate all available hour strings from company config
  const allTimes = (() => {
    const start = parseInt(availableTimeRange[0]);
    const end = parseInt(availableTimeRange[1]);
    const times = [];
    for (let h = start; h <= end; h++) {
      const t = `${h.toString().padStart(2, '0')}:00`;
      if (!blockedTimes.includes(t)) times.push(t);
    }
    return times;
  })();

  // ── Form state ──────────────────────────────────────────────────────────────
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [procedure, setProcedure] = useState('');
  const [anyProfessional, setAnyProfessional] = useState(true);
  const [resource, setResource] = useState('');
  const [preferredDays, setPreferredDays] = useState([]);
  const [selectedPeriods, setSelectedPeriods] = useState([]);
  const [specificTimes, setSpecificTimes] = useState([]);
  const [priority, setPriority] = useState('MEDIUM');
  const [startDate, setStartDate] = useState(getCurrentDateString());
  const [endDate, setEndDate] = useState(getDefaultEndDateString(availabilityParams?.daysLimit));
  const [observations, setObservations] = useState('');
  // Modalidade pedida: vai junto para o agendamento feito a partir da lista
  const [attendanceMode, setAttendanceMode] = useState(ATTENDANCE_IN_PERSON);
  const [meetingUrl, setMeetingUrl] = useState('');
  const [meetingUrlError, setMeetingUrlError] = useState('');
  const isOnline = attendanceMode === ATTENDANCE_ONLINE;

  // ── UI state ────────────────────────────────────────────────────────────────
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [patientsList, setPatientsList] = useState([]);
  const [sortedPatients, setSortedPatients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [apiError, setApiError] = useState('');

  // ── Load patients ───────────────────────────────────────────────────────────
  useEffect(() => {
    setLoading(true);
    GetPatients(false, (result) => {
      setPatientsList(result);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    if (patientsList.length > 0) {
      setSortedPatients(
        patientsList
          .filter(p => p.status == '1')
          .sort((a, b) => a.name.localeCompare(b.name))
          .map(p => ({ id: p.id, label: p.name }))
      );
    }
  }, [patientsList]);

  // ── Default procedure/resource ──────────────────────────────────────────────
  useEffect(() => {
    if (!initialData) {
      if (CompanyConfig.Procedures?.length > 0) {
        setProcedure(CompanyConfig.Procedures.find(p => p.Active == 1)?.Id || '');
      }
    }
  }, [CompanyConfig, initialData]);

  // ── Load edit data ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!initialData) return;

    const patient = GetPatientById(initialData.patientId);
    if (patient) setSelectedPatient({ id: patient.id, label: patient.name });

    setProcedure(initialData.procedureId || '');
    if (initialData.resourceId) {
      setAnyProfessional(false);
      setResource(initialData.resourceId);
    } else {
      setAnyProfessional(true);
      setResource('');
    }
    setPriority(initialData.priority || 'MEDIUM');
    setStartDate(initialData.startDate || getCurrentDateString());
    setEndDate(initialData.endDate || getDefaultEndDateString());
    setObservations(initialData.observations || '');
    const mode = normalizeAttendanceMode(initialData.attendance_mode);
    setAttendanceMode(mode);
    setMeetingUrl(mode === ATTENDANCE_ONLINE ? String(initialData.meeting_url || '') : '');

    try {
      const days = typeof initialData.preferredDays === 'string'
        ? JSON.parse(initialData.preferredDays)
        : initialData.preferredDays || [];
      setPreferredDays(days);
    } catch { setPreferredDays([]); }

    try {
      const times = typeof initialData.preferredTimes === 'string'
        ? JSON.parse(initialData.preferredTimes)
        : initialData.preferredTimes || [];
      setSpecificTimes(times);
      setSelectedPeriods(detectPeriodsFromTimes(times, allTimes));
    } catch { setSpecificTimes([]); }
  }, [initialData]);

  // ── Handlers ────────────────────────────────────────────────────────────────

  const toggleDay = useCallback((day) => {
    setPreferredDays(prev =>
      prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]
    );
    setValidationError('');
  }, []);

  const toggleAllDays = useCallback(() => {
    setPreferredDays(prev =>
      prev.length === availableDaysOfWeek.length ? [] : [...availableDaysOfWeek]
    );
    setValidationError('');
  }, [availableDaysOfWeek]);

  const togglePeriod = useCallback((periodId) => {
    setSelectedPeriods(prev => {
      let next;
      if (periodId === 'any') {
        next = prev.includes('any') ? [] : ['any'];
      } else {
        const withoutAny = prev.filter(p => p !== 'any');
        next = withoutAny.includes(periodId)
          ? withoutAny.filter(p => p !== periodId)
          : [...withoutAny, periodId];
      }
      // Sync specific times with period selection
      setSpecificTimes(expandPeriodsToTimes(next, allTimes));
      return next;
    });
    setValidationError('');
  }, [allTimes]);

  const toggleSpecificTime = useCallback((time) => {
    setSpecificTimes(prev => {
      const next = prev.includes(time) ? prev.filter(t => t !== time) : [...prev, time];
      setSelectedPeriods(detectPeriodsFromTimes(next, allTimes));
      return next;
    });
    setValidationError('');
  }, [allTimes]);

  // ── Validation ──────────────────────────────────────────────────────────────
  function validate() {
    if (!selectedPatient) { setValidationError('Selecione um paciente'); return false; }
    if (preferredDays.length === 0) { setValidationError('Selecione ao menos um dia'); return false; }
    const finalTimes = specificTimes.length > 0 ? specificTimes : expandPeriodsToTimes(selectedPeriods, allTimes);
    if (finalTimes.length === 0) { setValidationError('Selecione ao menos um período ou horário'); return false; }
    if (isOnline && !isValidMeetingUrl(meetingUrl)) { setMeetingUrlError(MEETING_URL_ERROR); return false; }
    return true;
  }

  // ── Build payload ───────────────────────────────────────────────────────────
  function buildPayload() {
    const finalTimes = specificTimes.length > 0
      ? [...specificTimes].sort()
      : expandPeriodsToTimes(selectedPeriods, allTimes).sort();

    return {
      companyId: CompanyConfig.Id,
      patientId: selectedPatient.id,
      procedureId: procedure,
      resourceId: anyProfessional ? null : resource,
      startDate,
      endDate,
      preferredDays,
      preferredTimes: finalTimes,
      priority,
      observations,
      attendance_mode: attendanceMode,
      // Vazio limpa o link gravado (null não seria enviado)
      meeting_url: isOnline ? meetingUrl.trim() : '',
    };
  }

  function handleSave() {
    if (!validate()) return;
    setLoading(true);
    setApiError('');

    if (initialData) {
      const payload = { ...buildPayload(), id: initialData.id, companyId: initialData.companyId, status: initialData.status, updatedBy: UserData.id };
      UpdateWaitingList(payload, (response) => {
        setLoading(false);
        if (response.success) { OnSaved(response); OnHide(true); }
        else setApiError(response.error || 'Erro ao atualizar');
      });
    } else {
      const payload = { ...buildPayload(), createdBy: UserData.id };
      SetWaitingList(payload, (response) => {
        setLoading(false);
        if (response.success) { OnSaved(response); OnHide(true); }
        else setApiError(response.error || 'Erro ao adicionar');
      });
    }
  }

  // ── Render ───────────────────────────────────────────────────────────────────

  const activeResources = (CompanyConfig.Resources || []).filter(r => r.Active == 1);
  const activeProcedures = (CompanyConfig.Procedures || []).filter(p => p.Active == 1);
  const allDaysSelected = preferredDays.length === availableDaysOfWeek.length;

  // Future appointments for selected patient
  const futureAppointments = useMemo(
    () => getFutureAppointments(appointments, selectedPatient?.id),
    [appointments, selectedPatient]
  );

  return (
    <Dialog
      open
      onClose={() => OnHide(false)}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: '16px', maxHeight: '90vh', display: 'flex', flexDirection: 'column' } }}
    >
      {/* Title */}
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1, pt: 2.5, px: 3 }}>
        <span style={{ fontSize: '1rem', fontWeight: 700, color: '#0f172a' }}>
          {initialData ? 'Editar paciente na lista' : 'Adicionar à lista de espera'}
        </span>
        <IconButton size="small" onClick={() => OnHide(false)} sx={{ color: '#94a3b8' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      {/* Scrollable content */}
      <DialogContent sx={{ px: 3, py: 0, overflowY: 'auto' }}>
        {apiError && <Alert severity="error" sx={{ mb: 2, mt: 1 }}>{apiError}</Alert>}

        {/* ── Section 1: Patient & appointment interest ── */}
        <div className={styles.section}>
          <div className={styles.sectionLabel}>Paciente e interesse</div>

          {/* Future appointments warning */}
          {futureAppointments.length > 0 && (
            <div className={styles.futureApptBanner}>
              <EventBusyIcon sx={{ fontSize: '0.9rem', flexShrink: 0 }} />
              <span>
                Este paciente já tem <strong>{futureAppointments.length} agendamento{futureAppointments.length > 1 ? 's' : ''} futuro{futureAppointments.length > 1 ? 's' : ''}</strong>.
                Considere prioridade baixa ou verifique se realmente precisa da lista de espera.
              </span>
            </div>
          )}

          {/* Patient */}
          <Autocomplete
            options={sortedPatients}
            getOptionLabel={o => o.label}
            isOptionEqualToValue={(o, v) => o.id == v.id}
            value={selectedPatient}
            onChange={(_, v) => { setSelectedPatient(v); setValidationError(''); }}
            disabled={!!initialData}
            loading={loading}
            renderOption={(props, option) => (
              <li {...props} key={option.id}><ListItemText primary={option.label} /></li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Paciente *"
                size="small"
                error={validationError.includes('paciente')}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading && <CircularProgress size={16} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            sx={{ mb: 2 }}
          />

          {/* Procedure + Priority side by side */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 10, marginBottom: 16 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Procedimento</InputLabel>
              <Select value={procedure} onChange={e => setProcedure(e.target.value)} label="Procedimento">
                {activeProcedures.map(p => (
                  <MenuItem key={p.Id} value={p.Id}>{p.Label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Prioridade</InputLabel>
              <Select value={priority} onChange={e => setPriority(e.target.value)} label="Prioridade">
                <MenuItem value="HIGH">Alta</MenuItem>
                <MenuItem value="MEDIUM">Média</MenuItem>
                <MenuItem value="LOW">Baixa</MenuItem>
              </Select>
            </FormControl>
          </div>

          {/* Professional preference */}
          <div className={styles.fieldLabel}>Profissional preferido</div>
          <div className={styles.toggleRow}>
            <button
              type="button"
              className={`${styles.toggleBtn} ${anyProfessional ? styles.toggleBtnActive : ''}`}
              onClick={() => { setAnyProfessional(true); setResource(''); }}
            >
              Qualquer profissional
            </button>
            <button
              type="button"
              className={`${styles.toggleBtn} ${!anyProfessional ? styles.toggleBtnActive : ''}`}
              onClick={() => setAnyProfessional(false)}
            >
              Escolher profissional
            </button>
          </div>
          {!anyProfessional && (
            <FormControl fullWidth size="small" sx={{ mt: 1.5 }}>
              <InputLabel>Profissional</InputLabel>
              <Select value={resource} onChange={e => setResource(e.target.value)} label="Profissional">
                {activeResources.map(r => (
                  <MenuItem key={r.Id} value={r.Id}>{r.Label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Modality: in person or online (optional meeting link) */}
          <div className={styles.fieldLabel} style={{ marginTop: 16 }}>Modalidade</div>
          <AttendanceModeSelector
            value={attendanceMode}
            onChange={(mode) => {
              setAttendanceMode(mode);
              if (mode !== ATTENDANCE_ONLINE) setMeetingUrl('');
              setMeetingUrlError('');
            }}
          />
          {isOnline && (
            <TextField
              label="Link da videochamada"
              type="url"
              size="small"
              fullWidth
              autoComplete="off"
              placeholder="https://... (opcional)"
              value={meetingUrl}
              onChange={e => { setMeetingUrl(e.target.value); setMeetingUrlError(''); }}
              error={Boolean(meetingUrlError)}
              helperText={meetingUrlError || undefined}
              inputProps={{ inputMode: 'url' }}
              sx={{ mt: 1.5 }}
            />
          )}
        </div>

        {/* ── Section 2: Availability ── */}
        <div className={styles.section}>
          <div className={styles.sectionLabel}>Quando o paciente pode vir?</div>

          {/* Days */}
          <div className={styles.fieldLabel} style={{ marginBottom: 8 }}>Dias da semana</div>
          <div className={styles.daysRow}>
            {[1, 2, 3, 4, 5, 6, 0]
              .filter(d => availableDaysOfWeek.includes(d))
              .map(d => (
                <button
                  key={d}
                  type="button"
                  className={`${styles.dayBtn} ${preferredDays.includes(d) ? styles.dayBtnActive : ''}`}
                  onClick={() => toggleDay(d)}
                >
                  {DAY_LABELS[d]}
                </button>
              ))}
            <button
              type="button"
              className={`${styles.dayBtn} ${styles.dayBtnAll} ${allDaysSelected ? styles.dayBtnAllActive : ''}`}
              onClick={toggleAllDays}
            >
              Todos
            </button>
          </div>
          {validationError.includes('dia') && (
            <div className={styles.errorText}>{validationError}</div>
          )}

          {/* Periods */}
          <div className={styles.fieldLabel} style={{ marginTop: 16, marginBottom: 8 }}>Período preferido</div>
          <div className={styles.periodsGrid}>
            {PERIODS.map(p => (
              <button
                key={p.id}
                type="button"
                className={`${styles.periodBtn} ${selectedPeriods.includes(p.id) ? styles.periodBtnActive : ''}`}
                onClick={() => togglePeriod(p.id)}
              >
                <span className={styles.periodLabel}>{p.label}</span>
                {p.sub && <span className={styles.periodSub}>{p.sub}</span>}
              </button>
            ))}
          </div>
          {validationError.includes('período') && (
            <div className={styles.errorText}>{validationError}</div>
          )}
        </div>

        {/* ── Section 3: Advanced options (collapsed) ── */}
        <div className={styles.advancedToggle} onClick={() => setAdvancedOpen(o => !o)}>
          <span>Opções avançadas</span>
          {advancedOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </div>

        <Collapse in={advancedOpen}>
          <div className={styles.section} style={{ paddingTop: 0 }}>

            {/* Specific hours */}
            <div className={styles.fieldLabel} style={{ marginBottom: 8 }}>Horários específicos</div>
            <div className={styles.timesGrid}>
              {allTimes.map(t => (
                <button
                  key={t}
                  type="button"
                  className={`${styles.timeBtn} ${specificTimes.includes(t) ? styles.timeBtnActive : ''}`}
                  onClick={() => toggleSpecificTime(t)}
                >
                  {t}
                </button>
              ))}
            </div>

            {/* Date range */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 16 }}>
              <TextField
                label="Data de início"
                type="date"
                size="small"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                label="Data de término"
                type="date"
                size="small"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </div>

            {/* Notes */}
            <TextField
              label="Observações"
              multiline
              rows={3}
              value={observations}
              onChange={e => setObservations(e.target.value)}
              fullWidth
              size="small"
              sx={{ mt: 2 }}
              placeholder="Informações adicionais..."
            />
          </div>
        </Collapse>

        {/* Spacer so content doesn't hide behind sticky footer */}
        <div style={{ height: 8 }} />
      </DialogContent>

      {/* Sticky footer */}
      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #f1f5f9', gap: 1 }}>
        {validationError && !validationError.includes('dia') && !validationError.includes('período') && (
          <Alert severity="error" sx={{ flex: 1, py: 0.5, fontSize: '0.8rem' }}>{validationError}</Alert>
        )}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <Button
            variant="outlined"
            onClick={() => OnHide(false)}
            disabled={loading}
            sx={{ textTransform: 'none', borderColor: '#e2e8f0', color: '#64748b', borderRadius: '8px', fontWeight: 600 }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={loading}
            sx={{
              textTransform: 'none',
              background: 'var(--color-blue-main, #5A80FB)',
              borderRadius: '8px',
              fontWeight: 700,
              boxShadow: 'none',
              '&:hover': { background: 'var(--color-main-dark, #26347B)' },
            }}
          >
            {loading ? <CircularProgress size={18} color="inherit" /> : (initialData ? 'Salvar alterações' : 'Adicionar')}
          </Button>
        </div>
      </DialogActions>
    </Dialog>
  );
}

export default AddWaitingListPopup;
